import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { Handshake, Send, Check, X, Trash2 } from "lucide-react";
import { logger } from "../../lib/logger";

export default function CollaborationsSection() {
  const { t } = useI18n();
  const [data, setData] = useState({ accepted: [], outgoing: [], incoming: [] });
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/org/me/collaborations");
      setData(data);
    } catch (err) {
      logger.warn("CollaborationsSection: failed to load collaborations", err);
    }
  };
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/org/me/collaborations/invite", { target_email: email.trim() });
      toast.success(t("collab.invited"));
      setEmail("");
      setShowInvite(false);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  const accept = async (id) => {
    try { await api.post(`/org/me/collaborations/${id}/accept`); toast.success(t("collab.accepted")); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };
  const decline = async (id) => {
    try { await api.post(`/org/me/collaborations/${id}/decline`); toast.success(t("collab.declined")); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };
  const remove = async (id) => {
    if (!confirm(t("collab.removeConfirm"))) return;
    try { await api.delete(`/org/me/collaborations/${id}`); toast.success(t("collab.removed")); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || t("dash.failed")); }
  };

  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5" data-testid="collaborations-section">
      <div className="flex items-center justify-between">
        <div className="font-display text-xl inline-flex items-center gap-2"><Handshake size={16} /> {t("collab.title")}</div>
        <button onClick={() => setShowInvite(!showInvite)} className="btn-primary" data-testid="collab-invite-btn">
          <Send size={12} /> {t("collab.inviteBtn")}
        </button>
      </div>

      {showInvite && (
        <form onSubmit={invite} className="mt-4 flex flex-wrap gap-2 items-end" data-testid="collab-invite-form">
          <div className="flex-1 min-w-[200px]">
            <div className="label-tech mb-2">{t("collab.targetEmail")}</div>
            <input type="email" className="input-tech" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="other@company.se" required data-testid="collab-email-input" />
          </div>
          <button type="submit" className="btn-primary" data-testid="collab-send-btn">{t("collab.send")}</button>
          <button type="button" onClick={() => setShowInvite(false)} className="btn-ghost">{t("admin.orgs.cancel")}</button>
        </form>
      )}

      {data.incoming?.length > 0 && (
        <div className="mt-6">
          <div className="label-tech mb-3">{t("collab.incoming")}</div>
          <div className="divide-y divide-[var(--ink-200)]">
            {data.incoming.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-2" data-testid={`collab-incoming-${o.id}`}>
                <div>
                  <div className="font-display">{o.name || o.id}</div>
                  <div className="label-tech">{o.owner_email}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => accept(o.id)} className="btn-ghost text-[var(--moss)]" data-testid={`collab-accept-${o.id}`}><Check size={12} /> {t("collab.accept")}</button>
                  <button onClick={() => decline(o.id)} className="btn-ghost text-[var(--brick)]" data-testid={`collab-decline-${o.id}`}><X size={12} /> {t("collab.decline")}</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.outgoing?.length > 0 && (
        <div className="mt-6">
          <div className="label-tech mb-3">{t("collab.outgoing")}</div>
          <div className="divide-y divide-[var(--ink-200)]">
            {data.outgoing.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-2" data-testid={`collab-outgoing-${o.id}`}>
                <div>
                  <div className="font-display">{o.name || o.id}</div>
                  <div className="label-tech">{o.owner_email}</div>
                </div>
                <button onClick={() => decline(o.id)} className="btn-ghost text-[var(--brick)]" data-testid={`collab-cancel-${o.id}`}>{t("collab.cancelInvite")}</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-6">
        <div className="label-tech mb-3">{t("collab.accepted")} ({data.accepted?.length || 0})</div>
        {(!data.accepted || data.accepted.length === 0) ? (
          <div className="label-tech">{t("collab.noneYet")}</div>
        ) : (
          <div className="divide-y divide-[var(--ink-200)]">
            {data.accepted.map((o) => (
              <div key={o.id} className="py-3 flex items-center justify-between gap-2" data-testid={`collab-accepted-${o.id}`}>
                <div>
                  <div className="font-display">{o.name || o.id}</div>
                  <div className="label-tech">{o.owner_email}</div>
                </div>
                <button onClick={() => remove(o.id)} className="btn-ghost text-[var(--brick)]" data-testid={`collab-remove-${o.id}`}><Trash2 size={12} /> {t("admin.orgs.delete")}</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
